import * as React from 'react';
import { FormControl, FormHelperText, FormLabel, Box } from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';
import { useMemo, useState, useEffect, useCallback, memo } from 'react';

interface PhoneProps {
    variant?: 'outlined' | 'filled' | 'standard';
    onChange: (value: string, info: any) => void;
    label?: string;
    description?: string;
    required?: boolean;
    value: string;
    error?: string;
    enabled?: boolean;
    visible?: boolean;
    options?: {
        whiteList?: string[];
        blackList?: string[];
        defaultCountry?: string;
    };
    appValidated?: boolean;
}

const Phone = memo((props: PhoneProps) => {
    const {
        variant = 'outlined',
        onChange,
        label,
        description,
        required = false,
        value = '',
        error,
        enabled = true,
        visible = true,
        options = {},
        appValidated = false
    } = props;

    const [appHasBeenValidated, setAppHasBeenValidated] = React.useState(false);

    React.useEffect(() => {
        if (appValidated) {
            setAppHasBeenValidated(true);
        }
    }, [appValidated]);

    const showRequiredError = useMemo(() =>
            required && appHasBeenValidated && !value,
        [required, appHasBeenValidated, value]
    );

    const [phoneValue, setPhoneValue] = useState(value || '');
    const [phoneInfo, setPhoneInfo] = useState<any>({
        countryCallingCode: '',
        countryCode: "AT",
        nationalNumber: "",
        numberValue: ""
    });

    // Sync with external value changes
    useEffect(() => {
        setPhoneValue(value || '');
    }, [value]);

    // Debounced onChange with cleanup
    useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(phoneValue, phoneInfo);
        }, 400);

        return () => clearTimeout(timeout);
    }, [phoneValue, phoneInfo, onChange]);

    // Memoize computed values
    const showDescription = useMemo(() =>
            description !== undefined && description !== "",
        [description]
    );

    const showError = useMemo(() =>
            error !== undefined && error !== "" && phoneValue !== "",
        [error, phoneValue]
    );

    const hasError = showError || showRequiredError;
    const errorMessage = showRequiredError ? 'This field is required' : error;

    const onlyCountries = useMemo(() =>
            (options.whiteList ?? []).map((item: string) => item.toUpperCase()),
        [options.whiteList]
    );

    const excludedCountries = useMemo(() =>
            (options.blackList ?? []).map((item: string) => item.toUpperCase()),
        [options.blackList]
    );

    // Memoize the change handler
    const onChangePhoneData = useCallback((val: string, info: any) => {
        setPhoneValue(val);
        setPhoneInfo(info);
    }, []);

    // Memoize MuiTelInput props to prevent unnecessary re-renders
    const telInputProps = useMemo(() => ({
        value: phoneValue,
        onChange: onChangePhoneData,
        defaultCountry: options.defaultCountry?.toUpperCase?.(),
        excludedCountries: excludedCountries.length ? excludedCountries : undefined,
        onlyCountries: onlyCountries.length ? onlyCountries : undefined,
        disabled: !enabled,
        disableFormatting: true,
        variant: variant,
        focusOnSelectCountry: false,
        format: "INTERNATIONAL" as const
    }), [
        phoneValue,
        onChangePhoneData,
        options.defaultCountry,
        excludedCountries,
        onlyCountries,
        enabled,
        variant
    ]);

    // Early return for hidden components
    if (!visible) {
        return null;
    }

    return (
        <Box sx={{ display: 'block' }}>
            <FormControl className="form-control" fullWidth={true}>
                <FormLabel required={required} color="secondary" className="form-separate-label">
                    {label}
                </FormLabel>
                <MuiTelInput {...telInputProps} />
                {showDescription && (
                    <FormHelperText>{description}</FormHelperText>
                )}
                {hasError && (
                    <FormHelperText error={true}>
                        {errorMessage}
                    </FormHelperText>
                )}
            </FormControl>
        </Box>
    );
});

Phone.displayName = 'Phone';

export default Phone;
